import dayjs from 'dayjs';
import { z } from 'zod';
import {
  EPaymentGateway,
  ELanguage,
  EUserRoles,
  EImageShape,
  EMessagingChannel,
  TRequiredUserField,
} from '@/types';
import {
  deviceIdSchema,
  emailSchemaRequired,
  firstNameSchema,
  generalTextSchema,
  lastNameSchema,
  nameForDeviceSchema,
  nameSchema,
  phoneSchema,
} from '@/utils/schemasZod';
import { isSystemService } from '@/utils/services';
import { getDateOnlyISOFormat } from '@/utils/convertDate';

const generalTextRegex = /^[a-zA-ZÀ-ÿ0-9\s(.,-/?:@=&$+`'ˆ~!*_)]*$/;

export const makeUserInviteSchema = (isInstitutionProvisioning: boolean) =>
  z
    .object({
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      email: emailSchemaRequired,
      institutionIds: z
        .array(z.string())
        .min(1, { message: 'zod.select-institution' }),
      role: z.string().min(1, { message: 'zod.select-role' }),
      billedInstitutionId: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        isInstitutionProvisioning &&
        data.role === EUserRoles.PROVIDER &&
        !data.billedInstitutionId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billedInstitutionId'],
          message: 'zod.select-billed-institution',
        });
      }
    });

export const organizationAdminInviteSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchemaRequired,
});

export const provideEmailSchema = z.object({
  email: emailSchemaRequired,
});

export const deviceInviteSchema = z.object({
  firstName: nameForDeviceSchema,
  lastName: z.literal(''),
  email: emailSchemaRequired,
  institutionIds: z
    .array(z.string())
    .min(1, { message: 'zod.select-institution' }),
  role: z.literal(EUserRoles.DEVICE),
  meta: z.object({
    deviceId: deviceIdSchema,
  }),
});

const insuranceSchema = z.object({
  id: z.string().optional(),
  name: generalTextSchema.min(3, {
    message: 'zod.title-must-contain-at-least-3',
  }),
  copayValue: z
    .number()
    .min(0)
    .max(99999, { message: 'zod.value-must-be-in-range' })
    .default(0),
});

const emailSchema = z
  .string()
  .min(1, { message: 'zod.field-required' })
  .email({ message: 'common.invalid-email' });

const phoneSchemaRequired = z
  .object({
    country: z.string().default('US'),
    number: z
      .string()
      .min(1, { message: 'zod.field-required' })
      .min(4, { message: 'zod.phone-number-must-contain-from-4-to-12-digits' })
      .max(12, { message: 'zod.phone-number-must-contain-from-4-to-12-digits' })
      .trim()
      .regex(/^[0-9 ()-]+$/, { message: 'zod.phone-format' })
      .default(''),
  })
  .superRefine((data, ctx) => {
    if (typeof data !== 'object') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'zod.field-required',
        path: [],
      });
    }
  });

const optionalPositiveNumberField = z
  .union([z.number(), z.string(), z.undefined()])
  .transform((val) => {
    if (val === '' || val === undefined) return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  })
  .pipe(
    z
      .number()
      .min(1, {
        message: 'zod.the-number-cannot-be-less-than-or-equal-to-zero',
      })
      .optional(),
  );

export const EMPTY_INSTITUTION_CONTRACT = {
  seats: undefined,
  stationLicenses: undefined,
  contractStartDate: '',
  contractEndDate: '',
};

export const institutionProvisioningSchema = z
  .object({
    contracts: z.array(
      z.object({
        id: z.string().optional(),
        seats: optionalPositiveNumberField,
        stationLicenses: optionalPositiveNumberField,
        contractStartDate: z.string(),
        contractEndDate: z.string(),
      }),
    ),
  })
  .superRefine(({ contracts }, ctx) => {
    const addIssue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

    contracts.forEach((contract, index) => {
      (['seats', 'stationLicenses', 'contractStartDate'] as const).forEach(
        (field) => {
          if (contract[field] === undefined || contract[field] === '') {
            addIssue(['contracts', index, field], 'zod.field-required');
          }
        },
      );
    });

    const today = getDateOnlyISOFormat(dayjs().utc());
    const activeContract = contracts.find(
      ({ id, contractStartDate, contractEndDate }) =>
        !!id &&
        dayjs(today).isBetween(contractStartDate, contractEndDate, 'day', '[]'),
    );
    contracts.forEach(({ id, contractStartDate }, index) => {
      if (
        id ||
        !activeContract ||
        !contractStartDate ||
        dayjs(contractStartDate).isAfter(activeContract.contractEndDate, 'day')
      ) {
        return;
      }
      addIssue(
        ['contracts', index, 'contractStartDate'],
        'zod.contract-start-date-before-active-end-date',
      );
    });

    const byStart = contracts
      .map((contract, index) => ({ ...contract, index }))
      .filter(({ contractStartDate }) => !!contractStartDate)
      .sort((a, b) => dayjs(a.contractStartDate).diff(b.contractStartDate));
    byStart.slice(1).forEach((contract, i) => {
      if (
        dayjs(contract.contractStartDate).isAfter(
          byStart[i].contractEndDate,
          'day',
        )
      ) {
        return;
      }
      addIssue(
        ['contracts', contract.index, 'contractStartDate'],
        'zod.contract-start-date-overlaps-previous',
      );
    });
  });

export type TInstitutionProvisioningForm = z.input<
  typeof institutionProvisioningSchema
>;

const gatewaySchema = z
  .object({
    type: z.nativeEnum(EPaymentGateway),
    merchantId: z.string().optional(),
    merchantName: z.string().optional(),
    authorizationKey: z.string().optional(),
    currency: z.string().optional(),
    active: z.boolean().optional(),
  })
  .refine((data) => !data.active || !!data.authorizationKey, {
    path: ['authorizationKey'],
    message: 'zod.field-required',
  })
  .refine((data) => !data.active || !!data.currency, {
    path: ['currency'],
    message: 'zod.field-required',
  })
  .refine((data) => !data.active || !!data.type, {
    path: ['type'],
    message: 'zod.field-required',
  })
  .refine(
    (data) =>
      !data.active || !!data.merchantId || data.type !== EPaymentGateway.AZUL,
    {
      path: ['merchantId'],
      message: 'zod.field-required',
    },
  )
  .refine(
    (data) =>
      !data.active || !!data.merchantName || data.type !== EPaymentGateway.AZUL,
    {
      path: ['merchantName'],
      message: 'zod.field-required',
    },
  );

const paymentSchema = z
  .object({
    scheduleReminder: optionalPositiveNumberField,
    intervals: optionalPositiveNumberField,
    enabled: z.boolean().optional(),
    enableReminder: z.boolean().optional(),
    gateways: z.array(gatewaySchema).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.enableReminder && data.scheduleReminder === undefined) {
      ctx.addIssue({
        path: ['scheduleReminder'],
        code: z.ZodIssueCode.custom,
        message: 'zod.field-required',
      });
    }

    if (data.enableReminder && data.intervals === undefined) {
      ctx.addIssue({
        path: ['intervals'],
        code: z.ZodIssueCode.custom,
        message: 'zod.field-required',
      });
    }

    if (data.enabled) {
      if (data.gateways.length < 1) {
        ctx.addIssue({
          path: ['gateways'],
          code: z.ZodIssueCode.custom,
          message: 'zod.field-required',
        });
      }

      data.gateways.forEach((gateway, index) => {
        if (!gateway.currency) {
          ctx.addIssue({
            path: ['gateways', index, 'currency'],
            code: z.ZodIssueCode.custom,
            message: 'zod.field-required',
          });
        }

        if (!gateway.authorizationKey) {
          ctx.addIssue({
            path: ['gateways', index, 'authorizationKey'],
            code: z.ZodIssueCode.custom,
            message: 'zod.field-required',
          });
        }
      });
    }
  })
  .optional();

const subdomainBaseSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9-]*$/, {
    message: 'zod.subdomain-can-only-contain',
  });

const subdomainRefinement = (value: string | undefined) => {
  if (!value) return true;
  return (
    value.toLowerCase() !== import.meta.env.VITE_DEFAULT_SUBDOMAIN.toLowerCase()
  );
};

const generalTextBaseSchema = z.string().trim().regex(generalTextRegex, {
  message: 'zod.invalid-general-text',
});

const whitelabelRest = {
  logoImageUrl: z.string().nullable().optional(),
  logoImageShape: z.nativeEnum(EImageShape).optional(),
  mobileHomePageLogoUrl: z.string().nullable().optional(),
  bannerImageUrl: z.string().nullable().optional(),
  coverImageUrl: z.string().nullable().optional(),
  scheduleBackgroundColor: z.string().optional(),
  scheduleTextColor: z.string().optional(),
  scheduleHighlightOneColor: z.string().optional(),
  scheduleHighlightTwoColor: z.string().optional(),
  isSolidSchedule: z.boolean().optional(),
  onDemandBackgroundColor: z.string().optional(),
  onDemandTextColor: z.string().optional(),
  onDemandHighlightOneColor: z.string().optional(),
  onDemandHighlightTwoColor: z.string().optional(),
  isSolidOnDemand: z.boolean().optional(),
  primaryColor: z.string().optional(),
  buttonTextColor: z.string().optional(),
  faviconImageUrl: z.string().nullable().optional(),
  isSolidAppointment: z.boolean().optional(),
  appointmentBackgroundColor: z.string().optional(),
  appointmentTextColor: z.string().optional(),
  appointmentHighlightOneColor: z.string().optional(),
  appointmentHighlightTwoColor: z.string().optional(),
};

const formSchema = z.object({
  templateId: z.string(),
  required: z.boolean().default(false),
});

const timerField = z
  .union([
    z.string(),
    z.number().min(1, {
      message: 'zod.the-number-cannot-be-less-than-or-equal-to-zero',
    }),
    z.null(),
  ])
  .transform((val) => {
    if (val === '' || val === undefined) return null;
    if (typeof val === 'string') {
      const num = Number(val);
      return isNaN(num) ? null : num;
    }
    return val;
  })
  .refine((val) => val === null || val >= 1, {
    message: 'zod.the-number-cannot-be-less-than-or-equal-to-zero',
  })
  .refine((val) => val === null || val <= 60, {
    message: 'zod.value-must-not-exceed-60',
  });

export const institutionSettingsSchema = z.object({
  id: z.string(),
  name: generalTextSchema
    .min(1, { message: 'zod.field-required' })
    .refine((val) => /\p{L}|\p{N}/u.test(val), {
      message: 'zod.name-must-contain-alphanumeric',
    }),
  language: z.nativeEnum(ELanguage),
  phone: phoneSchema.nullable().optional(),
  address1: z.string().trim().min(1, { message: 'zod.field-required' }),
  address2: z.string().nullable().optional(),
  city: generalTextSchema.trim().min(1, { message: 'zod.field-required' }),
  state: generalTextSchema.trim().min(1, { message: 'zod.field-required' }),
  country: generalTextSchema.trim().min(1, { message: 'zod.field-required' }),
  zip: generalTextSchema.trim().min(1, { message: 'zod.field-required' }),
  selfEnrollAccessCode: z.string(),
  pocSettings: z.object({
    city: generalTextSchema.optional(),
    name: nameSchema.trim().min(1, { message: 'zod.field-required' }),
    email: emailSchema,
    phone: phoneSchemaRequired,
    state: generalTextSchema.nullable().optional(),
    title: generalTextSchema.trim().min(1, { message: 'zod.field-required' }),
    address1: generalTextSchema.optional(),
    address2: generalTextSchema.optional(),
    country: generalTextSchema.nullable().optional(),
    zip: generalTextSchema.optional(),
  }),

  configuration: z
    .object({
      dosespot: z
        .object({
          adminUserId: z.number().optional(),
          clinicId: z.number().optional(),
          subscriptionKey: z.string().optional(),
          clinicKey: z.string().optional(),
          enabled: z.boolean().optional(),
        })
        .optional()
        .nullable(),
      enforceCountry: z.boolean().optional(),
      dispatcherOption: z.boolean().optional(),
      enableChatAppointmentTypes: z.boolean().optional(),
      interactiveTriage: z.boolean().optional(),
      canarySpeech: z.boolean().optional(),
      tfa: z
        .object({
          enabled: z.boolean(),
        })
        .optional(),
      kioskMode: z.boolean().optional(),
      enableSymptomsCollection: z.boolean().optional(),
      medwand: z.boolean().optional(),
      waitingRoomOption: z.boolean().optional(),
      disablePatientScheduling: z.boolean().optional(),
      requiredPatientFields: z.array(z.custom<TRequiredUserField>()),
      requiredProviderFields: z.array(z.custom<TRequiredUserField>()),
      repingTimes: z
        .union([z.number(), z.string(), z.undefined()])
        .transform((val) => {
          if (val === '' || val === undefined) return null;
          const num = Number(val);
          return isNaN(num) ? null : num;
        })
        .pipe(
          z
            .number()
            .min(1, {
              message: 'zod.the-number-cannot-be-less-than-or-equal-to-zero',
            })
            .max(25, { message: 'zod.reping-times-range' })
            .nullable()
            .refine((val) => val !== null, { message: 'zod.field-required' }),
        ),
      repingDelay: z
        .union([z.number(), z.string(), z.undefined()])
        .transform((val) => {
          if (val === '' || val === undefined) return null;
          const num = Number(val);
          return isNaN(num) ? null : num;
        })
        .pipe(
          z
            .number()
            .min(1, {
              message: 'zod.the-number-cannot-be-less-than-or-equal-to-zero',
            })
            .max(60, { message: 'zod.value-must-not-exceed-60' })
            .nullable()
            .refine((val) => val !== null, { message: 'zod.field-required' }),
        ),
      patientDataRetentionPeriod: z
        .union([z.number(), z.string(), z.undefined()])
        .transform((val) => {
          if (val === '' || val === undefined) return null;
          const num = Number(val);
          return isNaN(num) ? null : num;
        })
        .pipe(
          z
            .number()
            .int()
            .min(1, {
              message: 'zod.the-number-cannot-be-less-than-or-equal-to-zero',
            })
            .max(10, { message: 'zod.value-must-not-exceed-10' })
            .nullable()
            .refine((val) => val !== null, { message: 'common.required' }),
        ),
      dispatcherTimer: timerField,
      waitingRoomTimer: timerField,
      autoConfirm: z.boolean().optional(),
      advanceNoticeBuffer: z.number(),
      mobileMessagingChannel: z.nativeEnum(EMessagingChannel).optional(),
    })
    .refine(
      (data) => !data.waitingRoomOption || data.waitingRoomTimer !== null,
      {
        path: ['waitingRoomTimer'],
        message: 'common.required',
      },
    )
    .refine((data) => !data.dispatcherOption || data.dispatcherTimer !== null, {
      path: ['dispatcherTimer'],
      message: 'common.required',
    })
    .optional()
    .nullable(),

  whitelabel: z
    .discriminatedUnion('enabled', [
      z.object({
        enabled: z.literal(false),
        subdomain: subdomainBaseSchema.optional().refine(subdomainRefinement, {
          message: 'zod.using-default-subdomain-not-allowed',
        }),
        productName: generalTextBaseSchema.optional(),
        ...whitelabelRest,
      }),
      z.object({
        enabled: z.literal(true),
        subdomain: subdomainBaseSchema
          .min(1, 'zod.field-required')
          .refine(subdomainRefinement, {
            message: 'zod.using-default-subdomain-not-allowed',
          }),
        productName: generalTextBaseSchema.min(1, 'zod.field-required'),
        ...whitelabelRest,
      }),
    ])
    .optional()
    .nullable(),

  services: z
    .array(
      z
        .object({
          id: z.string().optional(),
          name: generalTextSchema.min(1, { message: 'zod.field-required' }),
          description: generalTextSchema,
          feeEnabled: z.boolean().optional(),
          fee: z
            .number()
            .max(99999, { message: 'zod.fee-must-be-in-range' })
            .default(0),
          appointmentDuration: z
            .array(z.number())
            .min(1, { message: 'zod.field-required' })
            .refine((values) => values.every((value) => value % 15 === 0), {
              message: 'zod.appointment-duration-must-be-multiple-of-15',
            }),
          userInstitutionRoleIds: z.array(z.string()).optional(),
          allowEncounterNow: z.boolean().optional().nullable(),
          enabled: z.boolean().optional(),
          specialty: z.string().min(1, { message: 'zod.specialty-required' }),
          forms: z.array(formSchema).optional().default([]),
          isDefault: z.boolean().optional(),
        })
        .refine((data) => !data.feeEnabled || data.fee > 0, {
          path: ['fee'],
          message: 'zod.fee-must-be-greater-than-zero',
        })
        .refine((data) => !isSystemService(data), {
          path: ['name'],
          message: 'zod.service-name-reserved',
        }),
    )
    .optional(),

  insurance: z
    .object({
      enabled: z.boolean().nullable(),
      insurances: z.array(insuranceSchema).optional(),
      premiumEnabled: z.boolean().optional().nullable(),
      premiumPackageName: z.string().optional().nullable(),
    })
    .refine((data) => !data.premiumEnabled || !!data.premiumPackageName, {
      path: ['premiumPackageName'],
      message: 'zod.field-required',
    })
    .refine(
      (data) =>
        !data.enabled ||
        (Array.isArray(data.insurances) && data.insurances.length > 0),
      {
        path: ['insurances'],
        message: 'zod.insurance-required',
      },
    )
    .nullable()
    .optional(),

  payment: paymentSchema,

  provisioning: institutionProvisioningSchema.optional(),
});

export const newInsuranceSchema = z.object({
  name: generalTextSchema.min(3, {
    message: 'zod.title-must-contain-at-least-3',
  }),
  copayValue: z
    .string()
    .refine((val) => val === '' || /^[0-9]+$/.test(val), {
      message: 'zod.value-must-be-a-number',
    })
    .transform((val) => (val === '' ? 0 : Number(val)))
    .refine((val) => val >= 0 && val <= 99999, {
      message: 'zod.co-pay-must-be-in-range',
    }),
});

export type InstitutionSettingsForm = z.infer<typeof institutionSettingsSchema>;
